import React, { useState, useRef, useEffect } from 'react';
import { authFetch } from '../auth';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function ChatPanel({ deckId, onDeckUpdated }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if ((!input.trim() && !files.length) || !deckId) return;
    const userMsg = input.trim();
    const attachedFiles = [...files];
    setInput('');
    setFiles([]);

    const displayMsg = attachedFiles.length
      ? `${userMsg || 'Use these files'}${attachedFiles.length ? ` [📎 ${attachedFiles.map(f => f.name).join(', ')}]` : ''}`
      : userMsg;

    const newMessages = [...messages, { role: 'user', content: displayMsg }];
    setMessages(newMessages);
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('message', userMsg || 'Incorporate the attached files into the presentation');
      formData.append('history', JSON.stringify(newMessages.slice(-10)));
      attachedFiles.forEach(f => formData.append('files', f));

      const res = await authFetch(`${API}/chat/${deckId}`, {
        method: 'POST',
        body: formData
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Chat failed'); }
      const data = await res.json();

      const assistantMsg = data.message || 'Done.';
      setMessages(prev => [...prev, { role: 'assistant', content: assistantMsg }]);

      if (data.type === 'deck_update') {
        onDeckUpdated(data.deck);
      }
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${e.message}` }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="chat-panel">
      <div className="chat-panel__header">Chat with AI</div>
      <div className="chat-panel__messages">
        {messages.length === 0 && (
          <p className="chat-panel__empty">
            Edit your deck with chat. Try:<br/>
            • "Make it more executive"<br/>
            • "Add a risk slide"<br/>
            • "Make slide 3 shorter"<br/><br/>
            📎 Attach files to add new content to the deck.
          </p>
        )}
        {messages.map((msg, i) => (
          <div key={i} className={`chat-msg chat-msg--${msg.role}`}>
            <span className="chat-msg__text">{msg.content}</span>
          </div>
        ))}
        {loading && <div className="chat-msg chat-msg--assistant"><span className="chat-msg__text">Thinking...</span></div>}
        <div ref={bottomRef} />
      </div>

      {files.length > 0 && (
        <div className="chat-panel__files">
          {files.map((f, i) => (
            <span key={i} className="chat-panel__file-tag">
              {f.name}
              <button onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))}>✕</button>
            </span>
          ))}
        </div>
      )}

      <div className="chat-panel__input">
        <button
          className="chat-panel__attach"
          onClick={() => fileRef.current?.click()}
          disabled={loading}
          title="Attach files"
          aria-label="Attach files"
        >📎</button>
        <input
          ref={fileRef}
          type="file"
          accept=".txt,.pdf,.docx,.xlsx,.xls,.pptx,.ppt,.png,.jpg,.jpeg,.gif,.webp"
          multiple
          hidden
          onChange={(e) => { setFiles(prev => [...prev, ...Array.from(e.target.files)]); e.target.value = ''; }}
        />
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') sendMessage(); }}
          placeholder="Ask for changes or attach files..."
          disabled={!deckId || loading}
          aria-label="Chat message"
        />
        <button onClick={sendMessage} disabled={!deckId || loading || (!input.trim() && !files.length)}>
          Send
        </button>
      </div>
    </div>
  );
}
